import React, { useCallback, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { apiService } from '../services/api';
import { Button } from './ui/button';
import { RefreshCw } from 'lucide-react';

interface PlaidReconnectButtonProps {
  integrationId: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  children?: React.ReactNode;
}

export const PlaidReconnectButton: React.FC<PlaidReconnectButtonProps> = ({
  integrationId,
  onSuccess,
  onError,
  variant = 'default',
  size = 'default',
  className,
  children = 'Reconnect',
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch update link token when button is clicked
  const fetchUpdateLinkToken = async () => {
    try {
      setLoading(true);
      const response = await apiService.createPlaidUpdateLinkToken(integrationId);
      setLinkToken(response.link_token);
    } catch (error) {
      console.error('Error fetching update link token:', error);
      if (onError) {
        onError(error instanceof Error ? error.message : 'Failed to initialize Plaid Link');
      }
      setLoading(false);
    }
  };

  // Handle successful Plaid Link update flow
  const handleOnSuccess = useCallback(
    async () => {
      try {
        // After user completes re-authentication, mark integration as reconnected
        await apiService.reconnectPlaidIntegration(integrationId);

        // Notify parent component of success
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error('Error completing reconnection:', error);
        if (onError) {
          onError(error instanceof Error ? error.message : 'Failed to reconnect bank account');
        }
      } finally {
        setLinkToken(null);
        setLoading(false);
      }
    },
    [integrationId, onSuccess, onError]
  );

  // Handle Plaid Link exit
  const handleOnExit = useCallback(
    (error: any) => {
      setLinkToken(null);
      setLoading(false);
      
      if (error) {
        console.error('Plaid Link exit with error:', error);
        if (onError) {
          onError(error.error_message || 'Failed to reconnect bank account');
        }
      }
    },
    [onError]
  );

  // Initialize Plaid Link
  const config = {
    token: linkToken,
    onSuccess: handleOnSuccess,
    onExit: handleOnExit,
  };

  const { open, ready } = usePlaidLink(config);

  // Handle button click
  const handleClick = async () => {
    if (!linkToken) {
      await fetchUpdateLinkToken();
      // The useEffect below will automatically open when ready
    } else if (ready) {
      open();
    }
  };

  // Open Plaid Link when ready and token is available
  React.useEffect(() => {
    if (ready && linkToken) {
      // Auto-open when ready after fetching the token
      open();
    }
  }, [ready, linkToken, open]);

  return (
    <Button
      onClick={handleClick}
      disabled={loading || (!ready && linkToken !== null)}
      variant={variant}
      size={size}
      className={className}
    >
      {loading ? (
        <>
          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          Connecting...
        </>
      ) : (
        children
      )}
    </Button>
  );
};
